// **React Imports
import React, { Suspense, useContext, useMemo } from 'react'

// **Third party Imports
import { useSelector } from 'react-redux'
import { Navigate, Outlet, useLocation, useParams } from 'react-router-dom'

// **MUI Imports
import { Box, styled } from '@mui/material'

// **Component Imports
import BreadCrumbsBar from '../components/breadcrumbs'
import Header from '../components/header'
import OverlayLoading from '../components/overlay-loading'
import SidePanel from '../components/sidepanel'
import SubscriptionUpgradeAlert from '../components/subscription-upgrade-alert'
import ErrorBoundary from '../pages/error'

// **Context Imports
import { SettingsContext } from '../context/setting'

// **Utils Imports
import {
  checkAppModeAccess,
  checkCandidateAccess,
  checkUserAccess,
  getJWTToken,
  isRoleExcluded,
  removeJWTToken,
  reverseEngineerURL
} from '../utils/utils'

// **Route Role Imports
import { routeRoles } from './routeRoles'
import { APP_MODE } from '../utils/constant'

// **Styled Component
const MainContainer = styled('main')(({ theme, contentWidth }) => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  ...(contentWidth === 'boxed'
    ? {
        margin: '0 auto',
        maxWidth: '1440px'
      }
    : {}),
  [theme.breakpoints.up('md')]: {
    padding: '24px 16px'
  },
  [theme.breakpoints.down('md')]: {
    padding: '16px 8px'
  }
}))

const PrivateRoute = () => {
  // hooks
  const params = useParams()
  const location = useLocation()

  // Context State
  const {
    settings: { contentWidth, removePadding }
  } = useContext(SettingsContext)

  // Redux State
  const currentlyActivePlan = useSelector(
    (state) => state.auth.currentlyActivePlan
  )

  const appMode = useSelector((state) => state.auth.appMode)

  // variable
  const reversedUrl = useMemo(() => {
    const url = reverseEngineerURL(
      location.pathname?.endsWith('/') && location.pathname !== '/'
        ? location.pathname.slice(0, -1)
        : location.pathname,
      params
    )
    return url
  }, [location.pathname, params])

  const jwtToken = useMemo(() => getJWTToken(), [])

  const routeRole = useMemo(() => routeRoles?.[reversedUrl], [reversedUrl])
  const plansExist = useMemo(() => routeRole?.plans, [routeRole?.plans])

  const plansInclude = useMemo(
    () => plansExist && plansExist?.includes(currentlyActivePlan?.planName),
    [plansExist, currentlyActivePlan?.planName]
  )
  const hasAccess = useMemo(() => {
    return routeRole?.roles
      ? checkUserAccess(
          routeRole.roles,
          routeRole?.allowAdmin === undefined ? true : routeRole?.allowAdmin
        )
      : routeRole?.rolesExcluded
      ? !isRoleExcluded(routeRole.rolesExcluded)
      : true
  }, [routeRole])

  const modeIncluded = useMemo(() => {
    return checkAppModeAccess(
      routeRoles?.[reversedUrl]?.modeIncluded || [APP_MODE.HIRING],
      appMode
    )
  }, [reversedUrl, appMode])

  const shouldRenderPath = useMemo(
    () => (!plansExist || plansInclude) && hasAccess && modeIncluded,
    [plansExist, plansInclude, hasAccess, modeIncluded]
  )

  if (!jwtToken) {
    return <Navigate to="/login" replace />
  } else if (!checkCandidateAccess()) {
    if (shouldRenderPath) {
      return (
        <React.Fragment>
          <Box className="private-route-wrapper-container">
            <SidePanel />
            <Box className="private-route-main-container-wrapper">
              <Header />
              <BreadCrumbsBar />
              <SubscriptionUpgradeAlert />
              <ErrorBoundary>
                <Suspense fallback={<OverlayLoading />}>
                  <MainContainer
                    contentWidth={contentWidth}
                    sx={{
                      ...(removePadding ? { padding: '0 !important' } : {})
                    }}
                    className="private-route-main-container"
                  >
                    <Outlet />
                  </MainContainer>
                </Suspense>
              </ErrorBoundary>
            </Box>
          </Box>
        </React.Fragment>
      )
    } else {
      return (
        <Navigate to={routeRoles?.[reversedUrl]?.redirectPath || '/'} replace />
      )
    }
  } else {
    removeJWTToken()
    return <Navigate to="/candidates/login" replace />
  }
}

export default PrivateRoute
